import React, { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Grid, Loader, Text } from "@pushpress/shared-components";
import { useGetProfileForCalendarSyncQuery } from "../CalendarSync-generated.hooks";
import { AppContext } from "../../../context/app";
import {
  defaultToastOptions,
  extractError,
  joinStrings,
} from "../../../utils/misc";
import UserAvatar from "../../../components/common/UserAvatar/UserAvatar";
import UnConnectPanel from "./UnConnectedPanel";
import ConnectedPanel from "./ConnectedPanel";
import {
  useGetCalendars,
  useGoogleSyncAuthorization,
} from "../../../apis/googleSyncApi.hooks";
import { useCalendarSync } from "../CalendarSyncProvider/CalendarSyncProvider";
import useUrlSearchParams from "../../../hooks/useUrlSearchParams";
import BackArrow from "../../../assets/img/icons/icon-arrow-east.svg";
import styles from "./CalendarSyncStart.module.scss";

const CalendarSyncStart = () => {
  const { userUuid } = useParams<{ userUuid: string }>();

  const { delayStatusCheck } = useUrlSearchParams<{
    delayStatusCheck?: string;
  }>();

  const { client } = useContext(AppContext);

  const { showStaffCalendars, syncGoogleCalendar } = useCalendarSync();

  const { data: { getProfile: profile } = {} } =
    useGetProfileForCalendarSyncQuery({
      variables: {
        clientUuid: client.uuid,
        userUuid,
      },
      onError: (error) => {
        toast.error(extractError(error), defaultToastOptions);
      },
    });

  const { data: isConnected, refetch: refetchGoogleSync } =
    useGoogleSyncAuthorization(userUuid);

  const { data: calendarData, refetch: refetchCalendarData } = useGetCalendars(
    userUuid,
    {
      enabled: isConnected,
    },
  );

  const handleGoBack = () => {
    document.location.href = `/users/edit/${userUuid}`;
  };

  const refetchAll = async () => {
    await refetchGoogleSync();
    await refetchCalendarData();
  };

  const handleEdit = () => {
    showStaffCalendars(userUuid, { onSuccess: refetchAll });
  };

  // after connecting to google calendar, the google redirects to the same page again with a query param delayStatusCheck=1
  // we use this to automatically start the sync process
  useEffect(() => {
    if (isConnected && delayStatusCheck === "1") {
      syncGoogleCalendar(userUuid);
    }
  }, [delayStatusCheck, userUuid, isConnected, syncGoogleCalendar]);

  if (!profile || isConnected === undefined || (!calendarData && isConnected)) {
    return (
      <Grid
        container
        item
        xs={12}
        marginY={3}
        alignItems="center"
        justifyContent="center"
      >
        <Loader />
      </Grid>
    );
  }

  const userName = joinStrings(profile.firstName, profile.lastName);

  return (
    <>
      <div className={styles.root}>
        <div className={styles.header}>
          <Button
            text={`Back to ${userName}`}
            buttonType="text"
            icon={BackArrow}
            iconLocation="left"
            className="mb-2"
            onClick={handleGoBack}
          />
        </div>
        <div className={styles.content}>
          <div className={styles.container}>
            <div className={styles.pageHeader}>
              <Text variant="heading-4" color="wolf-800">
                Google Calendar Sync
              </Text>
              <UserAvatar size="medium" data={profile} />
            </div>
            {isConnected ? (
              <ConnectedPanel
                userUuid={userUuid}
                firstName={profile.firstName!}
                calendarData={calendarData!}
                onSuccess={refetchAll}
                onEditClick={handleEdit}
              />
            ) : (
              <UnConnectPanel
                firstName={profile.firstName!}
                userUuid={userUuid}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CalendarSyncStart;
